/**
 * V11.5 (2026-05-12) — URL 模式二维码实时预览。
 *
 * BrandAssetsEditor 二维码列表里, URL mode 之前只显示 lucide <QrCode /> 占位 icon,
 * 用户报"我填的链接生成的 qrcode 无法显示"。这里用 qrcode npm 包前端实时生成 PNG
 * dataURL, <img src> 渲染, 跟最终海报合成的 QR 视觉一致 (黑白方块, error_correction
 * 跟 ai-service compositor.prepare_brand_assets 的 ERROR_CORRECT_M 对齐)。
 *
 * 用法: <UrlQrPreview url="https://example.com" size={36} />
 * 空 url 时回退 null (caller 自己决定 fallback)。
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import QRCode from "qrcode";

interface Props {
  url: string;
  /** 渲染尺寸 px (square). 默认 36 跟 BrandAssetsEditor 列表 row preview h-9 一致。 */
  size?: number;
  className?: string;
}

export function UrlQrPreview({ url, size = 36, className }: Props) {
  const { t } = useTranslation("poster");
  const [dataUrl, setDataUrl] = useState<string>("");

  useEffect(() => {
    let cancelled = false;
    if (!url) {
      setDataUrl("");
      return;
    }
    QRCode.toDataURL(url, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: size * 2, // 2x DPR
      color: { dark: "#000000", light: "#FFFFFF" },
    })
      .then((d) => {
        if (!cancelled) setDataUrl(d);
      })
      .catch(() => {
        if (!cancelled) setDataUrl("");
      });
    return () => {
      cancelled = true;
    };
  }, [url, size]);

  if (!dataUrl) return null;
  return (
    <img
      src={dataUrl}
      alt={t("brand.editor.qrPreviewAlt")}
      width={size}
      height={size}
      className={className}
      style={{ display: "block" }}
    />
  );
}
